// services/bookingDraft.service.ts
// قرارداد واقعی: BookingDraftController → api/booking/draft
import axios from 'axios';
import { apiClient } from '../../utils/apiClient';

import type {
  ApiEnvelope,
  BookingDraft,
  BookingDraftItem,
  DailyDisplayPrice,
  AddBookingDraftItemPayload,
  UpdateBookingDraftItemPayload,
  ServiceResult,
} from '../../types/bookingDraft';

const GENERIC_ERROR = 'خطایی رخ داد. لطفاً دوباره تلاش کنید.';
const DRAFT_REQUEST_TIMEOUT_MS = 20000;

export function isAbortError(err: unknown): boolean {
  if (axios.isCancel(err)) return true;
  if (axios.isAxiosError(err) && err.code === 'ERR_CANCELED') return true;
  return err instanceof Error && err.name === 'AbortError';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function pick<T>(source: Record<string, unknown> | null, ...keys: string[]): T | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key] as T;
    }
  }
  return undefined;
}

function toOptionalInt(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.floor(n) : null;
}

function toNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toDateOnly(value: unknown): string {
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    return match ? match[1] : value.trim();
  }
  const rec = asRecord(value);
  if (rec && rec.year != null && rec.month != null && rec.day != null) {
    const y = String(rec.year).padStart(4, '0');
    const m = String(rec.month).padStart(2, '0');
    const d = String(rec.day).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

function toChildAges(value: unknown, children: number): number[] {
  if (!Array.isArray(value)) {
    return Array.from({ length: Math.max(0, children) }, () => 0);
  }
  const ages = value.map((entry) => {
    if (typeof entry === 'number') return entry;
    const rec = asRecord(entry);
    return toNumber(pick(rec, 'age', 'Age'), 0);
  });
  if (ages.length === children) return ages;
  if (ages.length > children) return ages.slice(0, children);
  return [...ages, ...Array.from({ length: children - ages.length }, () => 0)];
}

function toDailyPrices(value: unknown): DailyDisplayPrice[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const rec = asRecord(entry);
    return {
      date: toDateOnly(pick(rec, 'date', 'Date')),
      displayPrice: toNumber(pick(rec, 'displayPrice', 'DisplayPrice')),
    };
  });
}

function looksLikeDraft(value: unknown): boolean {
  const rec = asRecord(value);
  if (!rec) return false;
  return (
    typeof pick(rec, 'draftCode', 'DraftCode') === 'string' ||
    Array.isArray(pick(rec, 'items', 'Items')) ||
    typeof pick(rec, 'accommodationName', 'AccommodationName') === 'string'
  );
}

export function normalizeDraft(raw: unknown): BookingDraft | null {
  const rec = asRecord(raw);
  if (!rec || !looksLikeDraft(rec)) return null;

  const itemsRaw = pick<unknown[]>(rec, 'items', 'Items') ?? [];
  const items: BookingDraftItem[] = Array.isArray(itemsRaw)
    ? itemsRaw.map((entry) => {
        const item = asRecord(entry) ?? {};
        const children = toNumber(pick(item, 'children', 'Children'));
        return {
          id: toNumber(pick(item, 'id', 'Id')),
          roomId: toNumber(pick(item, 'roomId', 'RoomId')),
          apiRoomId: (pick(item, 'apiRoomId', 'ApiRoomId') as string | number) ?? '',
          apiAccommodationId:
            (pick(item, 'apiAccommodationId', 'ApiAccommodationId') as string | number) ?? '',
          roomTitle: String(pick(item, 'roomTitle', 'RoomTitle') ?? ''),
          capacity: toNumber(pick(item, 'capacity', 'Capacity')),
          quantity: Math.max(1, toNumber(pick(item, 'quantity', 'Quantity'), 1)),
          adults: Math.max(1, toNumber(pick(item, 'adults', 'Adults'), 1)),
          children,
          displayPrice: toNumber(pick(item, 'displayPrice', 'DisplayPrice')),
          dailyDisplayPrices: toDailyPrices(pick(item, 'dailyDisplayPrices', 'DailyDisplayPrices')),
          childAges: toChildAges(pick(item, 'childAges', 'ChildAges'), children),
          roomsRemainingSnapshot: toNumber(
            pick(item, 'roomsRemainingSnapshot', 'RoomsRemainingSnapshot'),
          ),
          ratePlanId: (pick(item, 'ratePlanId', 'RatePlanId') as string | number | null) ?? null,
          ratePlanTitle: (pick(item, 'ratePlanTitle', 'RatePlanTitle') as string | null) ?? null,
          thumbnailUrl: (pick(item, 'thumbnailUrl', 'ThumbnailUrl') as string | null) ?? null,
          isAvailableSnapshot: Boolean(
            pick(item, 'isAvailableSnapshot', 'IsAvailableSnapshot') ?? true,
          ),
          snapshotAt: String(pick(item, 'snapshotAt', 'SnapshotAt') ?? ''),
        };
      })
    : [];

  return {
    id: toNumber(pick(rec, 'id', 'Id')),
    draftCode: String(pick(rec, 'draftCode', 'DraftCode') ?? ''),
    accommodationId: toNumber(pick(rec, 'accommodationId', 'AccommodationId')),
    zoneId: toNumber(pick(rec, 'zoneId', 'ZoneId')),
    accommodationName: String(pick(rec, 'accommodationName', 'AccommodationName') ?? ''),
    cityName: String(pick(rec, 'cityName', 'CityName') ?? ''),
    checkIn: toDateOnly(pick(rec, 'checkIn', 'CheckIn')),
    checkOut: toDateOnly(pick(rec, 'checkOut', 'CheckOut')),
    adults: toNumber(pick(rec, 'adults', 'Adults')),
    children: toNumber(pick(rec, 'children', 'Children')),
    totalRooms: toNumber(pick(rec, 'totalRooms', 'TotalRooms')),
    couponCode: (pick(rec, 'couponCode', 'CouponCode') as string | null) ?? null,
    notes: (pick(rec, 'notes', 'Notes') as string | null) ?? null,
    customerName: (pick(rec, 'customerName', 'CustomerName') as string | null) ?? null,
    customerPhone: (pick(rec, 'customerPhone', 'CustomerPhone') as string | null) ?? null,
    customerEmail: (pick(rec, 'customerEmail', 'CustomerEmail') as string | null) ?? null,
    currency: String(pick(rec, 'currency', 'Currency') ?? 'IRR'),
    subTotal: toNumber(pick(rec, 'subTotal', 'SubTotal')),
    discountAmount: toNumber(pick(rec, 'discountAmount', 'DiscountAmount')),
    finalTotal: toNumber(pick(rec, 'finalTotal', 'FinalTotal')),
    expiresAt: String(pick(rec, 'expiresAt', 'ExpiresAt') ?? ''),
    remainingSeconds: toOptionalInt(pick(rec, 'remainingSeconds', 'RemainingSeconds')),
    items,
  };
}

function unwrap<T>(raw: unknown): ApiEnvelope<T> {
  const root = asRecord(raw);
  const payload = (pick(root, 'value', 'Value', 'result', 'Result') ?? raw) as unknown;
  const payloadRecord = asRecord(payload);
  const nestedData = pick<T>(payloadRecord, 'data', 'Data');
  const successFlag = pick<boolean>(payloadRecord, 'isSuccess', 'IsSuccess');
  const message = pick<string>(payloadRecord, 'message', 'Message');

  const resolvedData = (nestedData ??
    (looksLikeDraft(payload) ? (payload as T) : nestedData)) as T;

  const isSuccess =
    typeof successFlag === 'boolean' ? successFlag : resolvedData != null;

  return {
    isSuccess,
    message: typeof message === 'string' ? message : null,
    data: resolvedData,
  };
}

function backendMessage(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const body = asRecord(err.response?.data);
  const nested = asRecord(pick(body, 'value', 'Value'));
  const result = asRecord(pick(body, 'result', 'Result'));
  const msg = pick<string>(body, 'message', 'Message')
    ?? pick<string>(nested, 'message', 'Message')
    ?? pick<string>(result, 'message', 'Message');
  return typeof msg === 'string' && msg.trim() ? msg : undefined;
}

function statusMessage(status?: number): string {
  switch (status) {
    case 400:
      return 'درخواست نامعتبر است. اطلاعات رزرو را بررسی کنید.';
    case 401:
      return 'نشست شما منقضی شده است. لطفاً دوباره وارد شوید.';
    case 404:
      return 'پیش‌رزرو پیدا نشد یا منقضی شده است.';
    case 409:
      return 'اتاق دیگر موجود نیست یا قیمت تغییر کرده است.';
    case 422:
      return 'اطلاعات رزرو نامعتبر است.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'خطای سرور رخ داد. لطفاً کمی بعد دوباره تلاش کنید.';
    default:
      return GENERIC_ERROR;
  }
}

function toErrorResult<T>(err: unknown): ServiceResult<T> {
  if (isAbortError(err)) {
    return { ok: false, aborted: true, message: 'درخواست لغو شد.' };
  }

  const timedOut =
    axios.isAxiosError(err) &&
    (err.code === 'ECONNABORTED' || /timeout/i.test(String(err.message ?? '')));
  if (timedOut) {
    return {
      ok: false,
      status: axios.isAxiosError(err) ? err.response?.status : undefined,
      message: 'زمان انتظار برای دریافت اطلاعات به پایان رسید.',
    };
  }

  if (!axios.isAxiosError(err) || !err.response) {
    return {
      ok: false,
      message: 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.',
    };
  }

  const status = err.response.status;
  return {
    ok: false,
    status,
    message: backendMessage(err) || statusMessage(status),
  };
}

function requestConfig(signal?: AbortSignal) {
  return { signal, timeout: DRAFT_REQUEST_TIMEOUT_MS };
}

function readServerDate(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const rec = headers as { get?: (name: string) => unknown } & Record<string, unknown>;
  const headerValue = typeof rec.get === 'function' ? rec.get('date') : rec.date ?? rec.Date;
  return typeof headerValue === 'string' && headerValue.trim() ? headerValue : undefined;
}

function draftResult(
  env: ApiEnvelope<unknown>,
  serverDate?: string,
): ServiceResult<BookingDraft> {
  if (!env.isSuccess) {
    return {
      ok: false,
      message: env.message ?? GENERIC_ERROR,
      data: undefined,
      serverDate,
    };
  }
  const draft = normalizeDraft(env.data);
  if (!draft) {
    return { ok: false, message: 'پاسخ سرور نامعتبر است.', serverDate };
  }
  return { ok: true, data: draft, message: env.message ?? undefined, serverDate };
}

export async function getCurrentDraft(
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft | null>> {
  try {
    const response = await apiClient.get('/booking/draft/current', requestConfig(signal));
    const serverDate = readServerDate(response.headers);
    const env = unwrap<unknown>(response.data);

    if (env.isSuccess && (env.data == null || env.data === undefined)) {
      return { ok: true, data: null, message: env.message ?? undefined, serverDate };
    }

    if (env.isSuccess && !looksLikeDraft(env.data)) {
      return { ok: true, data: null, message: env.message ?? undefined, serverDate };
    }

    return draftResult(env, serverDate);
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return { ok: true, data: null };
    }
    return toErrorResult(err);
  }
}

export async function addBookingDraftItem(
  payload: AddBookingDraftItemPayload,
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft>> {
  try {
    const response = await apiClient.post('/booking/draft/items', payload, requestConfig(signal));
    return draftResult(unwrap(response.data), readServerDate(response.headers));
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}

export async function updateBookingDraftItem(
  payload: UpdateBookingDraftItemPayload,
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft>> {
  try {
    const response = await apiClient.put(
      `/booking/draft/items/${payload.itemId}`,
      payload,
      requestConfig(signal),
    );
    return draftResult(unwrap(response.data), readServerDate(response.headers));
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}

export async function removeBookingDraftItem(
  itemId: number,
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft | null>> {
  try {
    const { data } = await apiClient.delete(
      `/booking/draft/items/${itemId}`,
      requestConfig(signal),
    );
    const env = unwrap<boolean>(data);
    if (!env.isSuccess) {
      return { ok: false, message: env.message ?? 'حذف این اتاق با خطا مواجه شد.' };
    }
    return getCurrentDraft(signal);
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}

export async function recalculateBookingDraft(
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft>> {
  try {
    const response = await apiClient.post('/booking/draft/recalculate', {}, requestConfig(signal));
    return draftResult(unwrap(response.data), readServerDate(response.headers));
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}

export async function updateDraftCustomer(
  payload: {
    firstName: string;
    lastName: string;
    phone: string;
    nationalCode: string;
    email?: string;
  },
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft>> {
  try {
    const response = await apiClient.post(
      '/booking/draft/customer',
      payload,
      requestConfig(signal),
    );
    const env = unwrap<unknown>(response.data);
    if (!env.isSuccess) {
      return { ok: false, message: env.message ?? GENERIC_ERROR };
    }

    const rec = asRecord(env.data);
    const draftRaw = rec
      ? (pick(rec, 'draft', 'Draft') ?? env.data)
      : env.data;
    const draft = normalizeDraft(draftRaw);
    if (!draft) {
      return { ok: false, message: env.message ?? 'پاسخ سرور نامعتبر است.' };
    }

    const userCreated = Boolean(pick(rec, 'userCreated', 'UserCreated'));
    if (userCreated && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('authChanged'));
    }

    return { ok: true, data: draft, message: env.message ?? undefined };
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}

export async function applyBookingCoupon(
  couponCode: string,
  signal?: AbortSignal,
): Promise<ServiceResult<BookingDraft>> {
  try {
    const response = await apiClient.post(
      '/booking/draft/coupon',
      { couponCode },
      requestConfig(signal),
    );
    return draftResult(unwrap(response.data), readServerDate(response.headers));
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}

export async function clearBookingDraft(
  signal?: AbortSignal,
): Promise<ServiceResult<null>> {
  try {
    const { data } = await apiClient.delete('/booking/draft/clear', requestConfig(signal));
    const env = unwrap<boolean | null>(data);
    return { ok: env.isSuccess, data: null, message: env.message ?? undefined };
  } catch (err: unknown) {
    return toErrorResult(err);
  }
}
